import { useState, useEffect } from "react";
import API_BASE_URL from "../config";

const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
const currentUserId = currentUser?.userId;

const Toggle = ({ enabled, onToggle, label, description, icon }) => (
  <div
    onClick={onToggle}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 20px",
      borderRadius: "14px",
      background: enabled ? "#eff6ff" : "#f9fafb",
      border: `1.5px solid ${enabled ? "#2563eb" : "#e5e7eb"}`,
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginBottom: "10px",
      userSelect: "none",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
      <div
        style={{
          width: "42px",
          height: "42px",
          borderRadius: "12px",
          background: enabled
            ? "linear-gradient(135deg, #2563eb, #3b82f6)"
            : "#e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "20px",
          transition: "all 0.2s ease",
        }}
      >
        {icon}
      </div>
      <div>
        <div
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 600,
            fontSize: "15px",
            color: enabled ? "#1e3a5f" : "#6b7280",
            transition: "color 0.2s",
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "12px",
            color: enabled ? "#2563eb" : "#9ca3af",
            marginTop: "2px",
          }}
        >
          {description}
        </div>
      </div>
    </div>

    <div
      style={{
        width: "50px",
        height: "28px",
        borderRadius: "14px",
        background: enabled
          ? "linear-gradient(135deg, #2563eb, #3b82f6)"
          : "#d1d5db",
        position: "relative",
        transition: "background 0.2s ease",
        flexShrink: 0,
        boxShadow: enabled ? "0 2px 8px rgba(37,99,235,0.35)" : "none",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "4px",
          left: enabled ? "26px" : "4px",
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
          transition: "left 0.2s ease",
        }}
      />
    </div>
  </div>
);

export default function NotificationSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}api/NotificationSettings/user/${currentUserId}`,
        { headers: { accept: "text/plain" } }
      );
      if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
      const data = await res.json();
      setSettings(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}api/NotificationSettings/${settings.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", accept: "text/plain" },
          body: JSON.stringify({ ...settings, updatedAt: new Date().toISOString() }),
        }
      );
      if (!res.ok) throw new Error(`Failed to save: ${res.status}`);
      setSuccessMsg("Settings saved successfully!");
      setTimeout(() => setSuccessMsg(""), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 50%, #eff6ff 100%)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "48px 16px",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <div style={{ width: "100%", maxWidth: "480px" }}>

        {/* Header */}
        <div style={{ marginBottom: "28px" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "20px",
              padding: "5px 14px",
              marginBottom: "14px",
            }}
          >
            <span style={{ fontSize: "12px" }}>🔔</span>
            <span style={{ fontSize: "12px", fontWeight: 600, color: "#1d4ed8", letterSpacing: "0.03em" }}>
              User #{currentUserId || "—"}
            </span>
          </div>

          <h1
            style={{
              fontWeight: 700,
              fontSize: "26px",
              color: "#1e3a5f",
              margin: "0 0 4px",
              letterSpacing: "-0.02em",
            }}
          >
            Notification Settings
          </h1>
          <p style={{ color: "#9ca3af", fontSize: "14px", margin: 0 }}>
            Manage how and when you receive alerts
          </p>
        </div>

        {/* Main Card */}
        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "20px",
            padding: "24px",
            boxShadow: "0 4px 24px rgba(37,99,235,0.08)",
          }}
        >
          {loading ? (
            <div style={{ textAlign: "center", padding: "48px 0" }}>
              <div
                style={{
                  width: "38px",
                  height: "38px",
                  border: "3px solid #dbeafe",
                  borderTopColor: "#2563eb",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                  margin: "0 auto 14px",
                }}
              />
              <p style={{ color: "#9ca3af", fontSize: "14px", margin: 0 }}>Loading settings…</p>
            </div>

          ) : error && !settings ? (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{ fontSize: "36px", marginBottom: "10px" }}>⚠️</div>
              <p style={{ color: "#ef4444", fontSize: "14px", margin: "0 0 16px" }}>{error}</p>
              <button
                onClick={fetchSettings}
                style={{
                  background: "#eff6ff",
                  border: "1.5px solid #2563eb",
                  color: "#2563eb",
                  borderRadius: "10px",
                  padding: "8px 20px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "13px",
                }}
              >
                Try Again
              </button>
            </div>

          ) : settings ? (
            <>
              <Toggle
                enabled={settings.notice_allowed}
                onToggle={() => handleToggle("notice_allowed")}
                icon="🔔"
                label="Allow Notifications"
                description="Master switch for all notifications"
              />
              <Toggle
                enabled={settings.notice_sound}
                onToggle={() => handleToggle("notice_sound")}
                icon="🔊"
                label="Sound Alerts"
                description="Play a sound when notifications arrive"
              />
              <Toggle
                enabled={settings.notice_view}
                onToggle={() => handleToggle("notice_view")}
                icon="👁️"
                label="Visual Alerts"
                description="Show on-screen notification banners"
              />

              {error && (
                <div style={{ marginTop: "12px", padding: "10px 14px", borderRadius: "10px", background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: "13px" }}>
                  ⚠️ {error}
                </div>
              )}

              {successMsg && (
                <div style={{ marginTop: "12px", padding: "10px 14px", borderRadius: "10px", background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#16a34a", fontSize: "13px", fontWeight: 500 }}>
                  ✓ {successMsg}
                </div>
              )}

              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  marginTop: "20px",
                  width: "100%",
                  padding: "14px",
                  borderRadius: "12px",
                  background: saving ? "#93c5fd" : "linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)",
                  border: "none",
                  color: "#fff",
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 700,
                  fontSize: "15px",
                  cursor: saving ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: saving ? "none" : "0 4px 14px rgba(37,99,235,0.35)",
                }}
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </>
          ) : null}
        </div>

        {settings?.updatedAt && (
          <p style={{ textAlign: "center", color: "#d1d5db", fontSize: "11px", marginTop: "14px" }}>
            Last updated: {new Date(settings.updatedAt).toLocaleString()}
          </p>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}